import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { ChatMessage, ChatSession } from '../interfaces/chat.interface';
import { ChatService } from '../services/chat.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { UserStore } from './user.store';

type ChatState = {
  chatSession: ChatSession | null;
  messages: ChatMessage[];
  isLoading: boolean;
  pending: Map<string, boolean>; // store chat session ids that are pending a response. This is used to block sending messages to a chat session until a response is received.
  userInput: Map<string, string>; // store user input for each chat session
};

const initialState: ChatState = {
  chatSession: null,
  messages: [],
  isLoading: false,
  pending: new Map<string, boolean>(),
  userInput: new Map<string, string>(),
};

export const ChatStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      chatService = inject(ChatService),
      messageService = inject(NzMessageService),
      userStore = inject(UserStore),
    ) => {
      return {
        async getMessagesBySessionId(sessionId: string) {
          const userId = userStore.user()?.id;
          chatService.createChatSession(sessionId, userId).subscribe({
            next: (value) => {
              patchState(store, {
                chatSession: value,
                messages: value.messages ?? [],
              });
            },
            error: (error) => {
              console.error(error);
            },
          });
        },

        addMessage(conversation: ChatMessage, connection_id: string) {
          if (conversation.chat_session_id === store.chatSession()?.id) {
            if (store.messages() === undefined) {
              patchState(store, { messages: [] });
            }
            const newPending = new Map(store.pending());
            newPending.set(conversation.chat_session_id, true);
            patchState(store, (state) => ({
              messages: [...state.messages, conversation],
              pending: newPending,
            }));
          }

          const sessionId = store.chatSession()?.session_id;
          if (sessionId) {
            chatService
              .sendMessage(conversation, connection_id, sessionId)
              .subscribe({
                next: (value) => {},
                error: (error) => {
                  messageService.error('Failed to send message');
                  this.removeMessage(conversation.id);
                },
              });
          }
        },

        receiveMessage(message: ChatMessage) {
          const newPending = new Map(store.pending());
          newPending.delete(message.chat_session_id);

          if (message.chat_session_id === store.chatSession()?.id) {
            patchState(store, (state) => ({
              messages: [...state.messages, message],
              pending: newPending,
            }));
          } else {
            patchState(store, { pending: newPending });
          }
        },

        // 🔹 NEW: Handle incremental assistant tokens
        addOrUpdateStreamingMessage(chatSessionId: string, token: string) {
          if (chatSessionId !== store.chatSession()?.id) return;

          const messages = store.messages();
          let last = messages[messages.length - 1];

          if (
            !last ||
            last.sender !== 'bot' ||
            (last as any).isStreaming !== true
          ) {
            // Start a new assistant streaming message
            const newMsg: ChatMessage = {
              id: crypto.randomUUID(),
              chat_session_id: chatSessionId,
              message: token,
              sender: 'bot',
              createdAt: new Date(),
              // extra flag so we know it's streaming
              isStreaming: true as any,
            };
            patchState(store, { messages: [...messages, newMsg] });
          } else {
            // Append token to last assistant message
            last = { ...last, message: last.message + token };
            patchState(store, { messages: [...messages.slice(0, -1), last] });
          }
        },

        // 🔹 NEW: Finalize the assistant message after "stream_end"
        finalizeStreamingMessage(chatSessionId: string) {
          if (chatSessionId !== store.chatSession()?.id) {
            // Always clear pending for that session, even if not current
            const newPending = new Map(store.pending());
            newPending.delete(chatSessionId);
            patchState(store, { pending: newPending });
            return;
          }

          const messages = store.messages();
          let last = messages[messages.length - 1];

          const newPending = new Map(store.pending());
          newPending.delete(chatSessionId);

          if (last && last.sender === 'bot' && (last as any).isStreaming) {
            const finalized = { ...last };
            delete (finalized as any).isStreaming;

            patchState(store, {
              messages: [...messages.slice(0, -1), finalized],
              pending: newPending,
            });
          } else {
            patchState(store, { pending: newPending });
          }
        },

        removeMessage(bookId: string) {
          patchState(store, (state) => ({
            messages: state.messages.filter((msg) => msg.id !== bookId),
          }));
        },

        setUserInput(chatSessionId: string, input: string) {
          const newUserInput = new Map(store.userInput());
          newUserInput.set(chatSessionId, input);
          patchState(store, { userInput: newUserInput });
        },

        getUserInput(chatSessionId: string): string {
          return store.userInput().get(chatSessionId) ?? '';
        },

        reset() {
          patchState(store, initialState);
        },
      };
    },
  ),
);
